/**
 * LayoutSwitchDialog Component
 *
 * 3-option confirmation dialog (Save, Discard, Cancel) shown when the user
 * attempts to switch layouts while there are unsaved changes and autosave
 * is OFF.
 *
 * Follows DeleteLayoutModal pattern: isSubmitting state, reset on open,
 * try/catch with toast on error, onClose on success.
 *
 * Mounted outside the Popover in LayoutManagerControl to survive popover
 * teardown.
 */

// 1. External dependencies
import React, { useState, useEffect } from 'react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  useToastNotification,
} from '@enfomentis/ui';

// ===== TYPES =====

export interface LayoutSwitchDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Called on Cancel or after successful Save/Discard */
  onClose: () => void;
  /** Saves current layout then applies target layout */
  onSave: () => Promise<void>;
  /** Applies target layout without saving current changes */
  onDiscard: () => Promise<void>;
  /** Name of the layout being switched to (displayed in body text) */
  targetLayoutName: string;
}

// ===== COMPONENT =====

export const LayoutSwitchDialog: React.FC<LayoutSwitchDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  onDiscard,
  targetLayoutName,
}) => {
  const [submittingAction, setSubmittingAction] = useState<'save' | 'discard' | null>(null);

  const { showToast } = useToastNotification();

  // Reset submitting state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSubmittingAction(null);
    }
  }, [isOpen]);

  const handleSave = async (): Promise<void> => {
    setSubmittingAction('save');
    try {
      await onSave();
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save and switch layout';
      showToast({ color: 'danger', title: 'Error', description: message });
    } finally {
      setSubmittingAction(null);
    }
  };

  const handleDiscard = async (): Promise<void> => {
    setSubmittingAction('discard');
    try {
      await onDiscard();
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to switch layout';
      showToast({ color: 'danger', title: 'Error', description: message });
    } finally {
      setSubmittingAction(null);
    }
  };

  const isSubmitting = submittingAction !== null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" placement="top-center">
      <ModalContent>
        <ModalHeader>Unsaved Changes</ModalHeader>
        <ModalBody>
          <p className="text-text-secondary">
            You have unsaved changes. Save before switching to &lsquo;{targetLayoutName}&rsquo;?
          </p>
        </ModalBody>
        <ModalFooter>
          <Button variant="flat" onPress={onClose} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="flat"
            color="warning"
            onPress={handleDiscard}
            isLoading={submittingAction === 'discard'}
            isDisabled={isSubmitting}
          >
            Discard
          </Button>
          <Button
            color="primary"
            onPress={handleSave}
            isLoading={submittingAction === 'save'}
            isDisabled={isSubmitting}
          >
            Save &amp; Switch
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
